import {
    ISignalsChartResponse,
    SignalsRequest,
} from "components/Signals/SignalsModels";
import type { GroupByOptionValue } from "components/Signals/SignalsModels";
import ChartCardStore from "../ChartCard/ChartCardStore";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import { EmptyStateVariants } from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import { SignalReportDatum } from "models/Reporting/ReportDatum";
import { DrilldownBehavior } from "../Drilldown/DrilldownTypes";
import { SlimLLMClassifierResponse } from "models/LLMs/LLMClassifier";
import type { StatusDescriptor } from "components/UI/StatusTags";
import type { DataAvailabilityPolicy } from "components/Signals/DataAvailability/DataAvailability";

/**
 * Context passed to chart transforms so they can adjust results based on compare mode or dates.
 * Fields are provided per request; most transforms ignore the optional extras.
 */
export type TransformContext = {
    isComparePrev: boolean;
    groupingConfigured: boolean;
    swapGroupingAndX?: boolean;
    totalConversations?: number;
    startDate?: moment.Moment;
    endDate?: moment.Moment;
    questionOrderMap?: Map<string, number>;
    questionResponseOrderMap?: Map<string, number>;
};

/**
 * Transforms a chart response before rendering.
 * Endpoint configs opt into transforms explicitly; there is no default transform.
 */
export type TransformFn = (
    res: ISignalsChartResponse,
    ctx: TransformContext,
) => ISignalsChartResponse;

/**
 * Configuration for rendering a data grid alongside a chart.
 * Defaults come from `defaultGridConfig` in DefaultConfigValues.ts (gridColumnType "Topics",
 * no-op builder, not following pagination).
 */
export type GridConfig = {
    gridColumnType:
        | "Classifier With Eddy"
        | "Question Response"
        | "Classifier Prevalence"
        | "Hipaa"
        | "Module Score"
        | "Question"
        | "Safety Events"
        | "Topics"
        | "Contact Type"
        | "Eddy by Contact Type"
        | "Trending Topics"
        | "Model Performance";
    dataGridBuilder: GridBuilder;
    followPagination?: boolean;
    selection?: GridSelectionConfig;
};

export type GridSelectionBehavior = "request" | "noRequest";

export type GridSelectionConfig = {
    behavior: GridSelectionBehavior;
    onSelect: (params: {
        chartStore: ChartCardStore;
        dataGridStore: AcxDataGridStore;
    }) => string[];
};

/**
 * Builder that populates a data grid store with the chart's current data.
 * Defaults to a no-op implementation (`defaultGridBuilder` in DefaultConfigValues.ts).
 */
export type GridBuilder = (
    dataGridStore: AcxDataGridStore,
    gridConfig: GridConfig,
    currentData: SignalReportDatum[],
    chartCardStore: ChartCardStore,
) => void;

/**
 * Applies last-mile tweaks to a signals request before it is sent.
 * Defaults to an identity function (`defaultRequestPatch` in DefaultConfigValues.ts).
 */
export type RequestPatch = (state: SignalsRequest) => SignalsRequest;

/**
 * Strategies for exporting chart data.
 * Defaults to "none" in the `defaultCapabilities` object in DefaultConfigValues.ts.
 */
export type ExportStrategy = "client-csv" | "server-export" | "none";

/**
 * Flags describing what interactions a chart supports.
 * Defaults are defined in `defaultCapabilities` in DefaultConfigValues.ts
 * (comparePrev false, renderModes ["#", "%"], spotlight true, kboAnalyze false,
 * viewInConvos false, viewTopics false, sqlView false, pngExport true,
 * csvExport "none", canSelectAllModules false, drilldown true).
 *
 * Only `comparePrev`, `renderModes`, `csvExport`, `canSelectAllModules`, `pointSelectMenu`, and
 * `objectFilters` are read at runtime. `spotlight`, `kboAnalyze`, `sqlView`, and `pngExport` are
 * declared and set across the config but nothing consumes them — the Spotlight and Analyze With
 * KBo cards render off the persisted base object's `showSpotlight` / `showAnalyzeWithKBo` columns
 * (see ChartCardStore), which the DbMigrator seeding owns. Changing those four here has no effect
 * on behavior; change the seeding. They are kept in sync with the seeded values anyway so the
 * config does not read as a contradiction.
 */
export type CapabilityFlags = {
    comparePrev: boolean;
    renderModes: Array<"#" | "%">;
    spotlight: boolean;
    kboAnalyze: boolean;
    sqlView: boolean;
    pngExport: boolean;
    csvExport: ExportStrategy;
    canSelectAllModules: boolean;
    pointSelectMenu: boolean;
    objectFilters: boolean;
};

/**
 * Scopes a chart's licensed-module selector (the `Module` / `Modules` selection controls) to a
 * subset of the org's modules, and optionally hides the control when there is nothing to choose
 * between.
 *
 * Matching is by category **name**, not `moduleCategoryId`: ModuleCategories is a per-tenant table,
 * so the ids differ between organizations. The backend scopes the same way (see
 * `ResolveQualityModuleIdsAsync`), so both sides key off the same literal.
 */
export type ModuleSelectorConfig = {
    /** Licensed-module category names the selector may offer, e.g. `["Quality"]`. */
    categories?: string[];
    /**
     * Hides the selector when the scoped pool holds one option or none — there is no choice to
     * make. The chart still resolves and requests that single module.
     */
    hideWhenSingleOption?: boolean;
};

/**
 * Mirrors the server's SkillGrouping enum. Values are the C# member names, which the server binds
 * from strings. Groups are questions with the "Question Group" answer type; individual skills are
 * every other scored question.
 */
export enum SkillGrouping {
    Individual = "Individual",
    Group = "Group",
}

/**
 * Adds a chart-level selector that switches the chart between individual skills and skill groups.
 * The choice is sent as `skillGrouping` and persisted in the chart's vizOptions, so the endpoint's
 * request must accept a `SkillGrouping`.
 */
export type SkillGroupingSelectorConfig = {
    options: { label: string; value: SkillGrouping }[];
    /** Used until the user picks. */
    defaultValue: SkillGrouping;
};

export type PointSelectActions = {
    askKbo: boolean;
    viewInConvos: boolean;
    viewTopics: boolean;
    // Theme Cards' counterpart to viewTopics: breaks a Theme point into the Primary Drivers that
    // resolved to it. The two are mutually exclusive in practice, because a Card reads exactly one
    // Theme contract and only one of the two breakdowns can describe its population.
    viewDrivers: boolean;
    viewData: boolean;
    // "View {nextLevelLabel}" zoom action. Endpoint opts in; leaf cells on a
    // zoom-capable chart show the action disabled when hasChildren is absent.
    viewLevel: boolean;
};

/**
 * Optional point-action overrides keyed by the TreeMap zoom depth. Depth 0 is
 * the initial chart, depth 1 is the first zoom, and so on. Unspecified
 * actions inherit the endpoint's base `pointSelectActions` configuration.
 */
export type PointSelectActionsByZoomLevel = Partial<
    Record<number, Partial<PointSelectActions>>
>;

export type ExportConfig = {
    allowColumnSelection?: boolean;
};

/**
 * Pendo tracking identifiers this endpoint's surfaces render, one field per surface. Pendo tags
 * elements by DOM id (the `#tag` form product writes in tickets), so each value is emitted as an
 * `id` attribute verbatim rather than derived from anything at runtime — product configures the
 * exact string on their side, and a generated id would silently stop matching if a title changed.
 *
 * Every field is optional: a surface with no id here renders without one and goes untracked.
 */
export type PendoIds = {
    /** The endpoint's tile in the Add Cards palette. */
    addCard?: string;
};

/**
 * Pagination settings for data grids.
 * Defaults to non-paginated (`defaultPagination` in DefaultConfigValues.ts) with other fields undefined.
 */
export type PaginationConfig = {
    paginated: boolean;
    pageSize?: number;
    lockSelected?: boolean;
};

/**
 * Enables grouping and provides the available grouping options for a chart.
 * Defaults (`defaultGroupingConfig` in DefaultConfigValues.ts) disable grouping
 * and provide no disabled self-dimensions.
 */
export type GroupingConfig = {
    enabled: boolean;
    ignoreGroupingOrderRule: boolean;
    disabledGroupByNames: GroupByOptionValue[];
    /**
     * Allows a force-stacked chart whose endpoint always returns a grouping
     * dimension to resolve slice ordering through the normal group-by catalog
     * without enabling interactive grouping or adding grouping request fields.
     */
    fixedGroupingOrderRule?: {
        groupByOption: GroupByOptionValue;
    };
};

export type CacheStrategy =
    | "filtersChange"
    | "selectionsChange"
    | "filtersOrSelectionsChange";

/**
 * Visual configuration for rendering a chart.
 * Defaults are defined in `defaultRenderOptions` in DefaultConfigValues.ts
 * (Signals palette, comparePrevPalette secondary/primary colors, forceStacked false,
 * legendVisibility "auto", isTemporal false, patientJourneyBadge false,
 * menu true, showAccuracy false, emptyStateVariant "empty"), preferSeriesInTooltip false,
 * dataGridNumVisibleRows 5, cacheKeyStrategy "filtersOrSelectionsChange".
 *
 * `cacheKeyStrategy` controls what inputs are included in cache keys (filters, selections, or both).
 * Cache entries are endpoint-scoped and may expire by TTL or be evicted by per-endpoint LRU limits.
 */
export type RenderOptions = {
    palette: string[] | undefined;
    comparePrevPalette: string[] | undefined;
    forceStacked: boolean;
    legendVisibility: "auto" | "bottom" | "none";
    isTemporal: boolean;
    patientJourneyBadge: boolean;
    menu: boolean;
    showAccuracy: boolean;
    emptyStateVariant: EmptyStateVariants;
    preferSeriesInTooltip: boolean; // Should we use the series in the tooltip over the generated subtitle?
    dataGridNumVisibleRows: number; // Fixed row count used to size data grid containers
    cacheKeyStrategy: CacheStrategy;
    /**
     * Metadata key to read from each chart point and use as the tooltip title. Use this when the
     * chart label is shortened or coded, but the hover should show a clearer label.
     */
    tooltipTitleMetadataKey?: string;
    /** Turn off "x/N eligible conversations" for charts where the total doesn't match the percent. */
    hideEligibleConversations?: boolean;
};

/**
 * Comprehensive configuration for how a chart behaves, renders, and fetches data.
 * Default shape is assembled in `defaultChartBehavior` in DefaultConfigValues.ts
 * (identity request patch, no transforms, no data grid, non-paginated,
 * default capabilities, default render options, and grouping disabled).
 */
export type EndpointBehaviorConfig = {
    requestPatch: RequestPatch;
    transforms: TransformFn[];
    dataGrid?: GridConfig;
    pagination: PaginationConfig;
    capabilities: CapabilityFlags;
    renderOptions: RenderOptions;
    /** Optional endpoint-specific policy for date-dependent data availability UI. */
    dataAvailability?: DataAvailabilityPolicy;
    grouping: GroupingConfig;
    exportConfig: ExportConfig;
    pointSelectActions: PointSelectActions;
    pointSelectActionsByZoomLevel: PointSelectActionsByZoomLevel;
    /**
     * Scopes this chart's module selector, or undefined to offer the org's full module pool (today's
     * behavior for every chart that does not declare it). See {@link ModuleSelectorConfig}.
     */
    moduleSelector?: ModuleSelectorConfig;
    /**
     * Adds a skill grouping selector beside the chart's other selectors, or undefined for none. See
     * {@link SkillGroupingSelectorConfig}.
     */
    skillGroupingSelector?: SkillGroupingSelectorConfig;
    /**
     * Adds a synthetic series in percent mode containing the remainder needed
     * for each stacked bar to total 100%. Count data remains unchanged.
     */
    percentRemainderSeries: boolean;
    /**
     * Lifecycle status advertised by this Card in its header and its Add Cards tile, or undefined
     * when it advertises none. Lives here rather than in a standalone endpoint list so a Card's
     * status is declared beside the rest of its behavior and resolves through the same
     * {@link getEndpointBehaviorConfig} the render sites already hold.
     */
    status?: StatusDescriptor;
    /**
     * Pendo identifiers for this Card's surfaces, or undefined when none are tracked. Declared here
     * for the same reason as {@link status}: the render sites already resolve this config, so they
     * read the id from the endpoint's own declaration instead of consulting a separate map.
     */
    pendo?: PendoIds;
    drilldown?: DrilldownBehavior;
    kboContextSuffix?: (dataPoint: {
        series: string | null;
        x?: string | number | null;
        /** The skill grouping the chart requested, for endpoints with a skill grouping selector. */
        skillGrouping?: SkillGrouping;
    }) => string;
    /** When true, use y_percenttotal (not y) as the Kbo conversation count for this chart. */
    kboUseYPercentTotal?: boolean;
    /**
     * Order-rule identifier (from the org's order-rule catalog) applied to the stack/series
     * dimension ("grouping"), independent of the chart's own x-axis order rule. Used by grouped
     * charts (e.g. grouped model results) to order the stacks via the standard "results" rule
     * while the bars use their own order rule.
     */
    stackOrderRuleIdentifier?: string;
    /**
     * Opt-in for the generic "reorder the chart's x-axis order-rule values" UI in the configure
     * drawer (internal users only). When present, the drawer renders a draggable list of the chart's
     * order-rule values, labeled via {@link ReorderConfig.loadOptions}, and persists the new order.
     * The grouped model-results chart is the first consumer; any chart can opt in by providing this.
     */
    reorder?: ReorderConfig;
    /**
     * Opt-in for the "...for {model}" classifier selector in the "Analyze With KBo" card. When present,
     * the card renders a model selector populated by {@link KboModelOptionsConfig.loadOptions}; the chart
     * owns how those classifiers are sourced (e.g. a classifier group's members). When omitted, the card
     * analyzes the chart's single `selectedIds.llmClassifier` (current behavior). Mirrors {@link reorder}.
     */
    kboModelOptions?: KboModelOptionsConfig;
};

/**
 * Resolves the point actions for a rendered TreeMap depth. Levels without an
 * override use the endpoint's normal point-action configuration.
 */
export function resolvePointSelectActions(
    config: Pick<
        EndpointBehaviorConfig,
        "pointSelectActions" | "pointSelectActionsByZoomLevel"
    >,
    zoomLevel?: number,
): PointSelectActions {
    const levelOverrides =
        zoomLevel === undefined
            ? undefined
            : config.pointSelectActionsByZoomLevel[zoomLevel];

    return {
        ...config.pointSelectActions,
        ...levelOverrides,
    };
}

export type ReorderOption = { id: string; name: string };

/**
 * A classifier offered in the KBo card's model selector. Extends {@link SlimLLMClassifierResponse}
 * with `analyzable`: only LLM models have reasoning for KBo to analyze, so Lucene (RBC) classifiers
 * in the same group are surfaced but disabled (`analyzable: false`) with an explanatory tooltip.
 */
export type KboModelOption = SlimLLMClassifierResponse & {
    analyzable: boolean;
};

export type KboModelOptionsConfig = {
    /**
     * Resolves the classifiers (and their results) selectable in the KBo card's model selector. Async
     * because membership may require a fetch. Returns {@link KboModelOption} so the card can reuse the
     * existing result-option logic / `analyzeModelResultsAction` and disable non-analyzable members.
     */
    loadOptions: (args: KboModelOptionsArgs) => Promise<KboModelOption[]>;
};

/**
 * Inputs handed to {@link KboModelOptionsConfig.loadOptions}. Plain data only (no store) so the loader
 * stays decoupled, matching the {@link ReorderConfig.loadOptions} convention.
 */
export type KboModelOptionsArgs = {
    dataEndpoint?: string;
    /** The chart's `selectedIds.classifierGroup` path (interactive grouped charts). */
    classifierGroup?: string | string[];
    /** The org's published LLM classifiers — the source of `results` for each option. */
    llmClassifiers: SlimLLMClassifierResponse[];
};

/**
 * Who is allowed to see/use reordering for a chart. Centralizes the audience policy so flipping a chart
 * from staff-only to generally-available is a one-line config change. Extend this union (and the
 * resolver in canReorderChart) if finer-grained audiences are needed later.
 */
export type ReorderAudience = "internal" | "everyone";

export type ReorderConfig = {
    /** Section header label, e.g. "Models". */
    label: string;
    /**
     * Resolves display names for the order-rule value ids of the chart being configured. Async because
     * labels may require a fetch. Functions in config are already established here (see {@link TransformFn}).
     */
    loadOptions: (args: { dataEndpoint?: string }) => Promise<ReorderOption[]>;
    /** Audience permitted to reorder. Defaults to {@link DEFAULT_REORDER_AUDIENCE} ("internal"). */
    audience?: ReorderAudience;
};

/**
 * Identifiers used to scope the current chart selection (e.g., classifiers, modules, topics).
 */
export type SelectedIds = {
    llmClassifier?: string;
    classifierIds?: string[];
    classifierCategoryId?: string;
    topicIds?: string[];
    moduleId?: string;
    moduleIds?: string[];
    questionIds?: string[];
    tagIds?: string[];
};
